/**
 * coachingLifecycle.ts (web) — the manager_flags coaching lifecycle, mirrored
 * from server/src/lib/coachingLifecycle.ts (sql/021). Keep the two in lock-step.
 *
 *     needs_review → assigned → acknowledged → follow_up → improved → closed
 *
 * Manager/admin transitions are strictly FORWARD along the chain (skips
 * allowed — e.g. needs_review → closed dismisses without coaching), plus ONE
 * reopen edge: closed → needs_review. 'acknowledged' is never a manager
 * target — it is the rep's "I saw this" stamp. The assigned REP gets exactly
 * one transition: assigned → acknowledged, reveal-gated server-side.
 * The server (PATCH /api/calls/:id/flag) is authoritative — these helpers only
 * drive which buttons/options the UI renders.
 */
import type { FlagLifecycleStatus } from "@/types";

export const FLAG_LIFECYCLE_STATUSES: readonly FlagLifecycleStatus[] = [
  "needs_review",
  "assigned",
  "acknowledged",
  "follow_up",
  "improved",
  "closed",
];

/** Every non-terminal status — "still in flight" (what /api/manager/queue returns). */
export const OPEN_FLAG_STATUSES: readonly FlagLifecycleStatus[] = [
  "needs_review",
  "assigned",
  "acknowledged",
  "follow_up",
  "improved",
];

export const FLAG_STATUS_LABELS: Record<FlagLifecycleStatus, string> = {
  needs_review: "Needs review",
  assigned: "Assigned",
  acknowledged: "Acknowledged",
  follow_up: "Follow-up",
  improved: "Improved",
  closed: "Closed",
};

/** Badge tone per status (matches components/ui Badge tones). */
export type FlagStatusTone = "muted" | "primary" | "success" | "warning" | "danger";

export const FLAG_STATUS_TONES: Record<FlagLifecycleStatus, FlagStatusTone> = {
  needs_review: "danger", // untriaged — wants manager attention
  assigned: "warning", // waiting on the rep's acknowledgement
  acknowledged: "primary", // rep saw it — coaching in progress
  follow_up: "primary",
  improved: "success",
  closed: "muted",
};

const ORDER = new Map<FlagLifecycleStatus, number>(
  FLAG_LIFECYCLE_STATUSES.map((s, i) => [s, i]),
);

/**
 * The manager/admin's legal transition TARGETS from a status: every later
 * status along the chain (skips allowed) EXCEPT the rep-only 'acknowledged',
 * or the reopen when closed. Mirrors the server's isLegalTransition exactly
 * (same-status no-ops are illegal).
 */
export function legalTransitionTargets(
  from: FlagLifecycleStatus,
): FlagLifecycleStatus[] {
  if (from === "closed") return ["needs_review"]; // reopen
  const fromIdx = ORDER.get(from) ?? Number.MAX_SAFE_INTEGER;
  return FLAG_LIFECYCLE_STATUSES.filter(
    (s) => s !== "acknowledged" && (ORDER.get(s) ?? -1) > fromIdx,
  );
}

/** The rep's single allowed transition edge (server + RLS enforce the same). */
export const REP_ACK_FROM: FlagLifecycleStatus = "assigned";
export const REP_ACK_TO: FlagLifecycleStatus = "acknowledged";
